import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { userService } from '@/services/user-service';
import { affiliateService } from '@/services/affiliate-service';
import { productService } from '@/services/product-service';
import { accountService } from '@/services/account-service';
import { contentfulService } from '@/services/contentful-service';
import { useCurrentUser } from '@/hooks/use-current-user';
import { usePageContext } from '@/contexts/page-context';
import { 
  Header, 
  ContentContainer, 
  Field,
  FieldLabel,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  toast,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Button,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Spinner,
  SearchInput
} from 'prosci-ui';
import {
  MoreHorizontal,
  Copy,
  Upload,
  UserPlus,
  Edit,
  Trash2,
  AlertCircle,
  UserRoundX,
  ChevronDown,
  UserCheck
} from '@/lib/icons';

export function UsersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: currentAdmin, isLoading: userLoading } = useCurrentUser();
  const { setShowPageHeader } = usePageContext();
  
  // Initialize filter state from URL search params
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [organizationSearch, setOrganizationSearch] = useState(() => searchParams.get('org') || '');
  const [selectedCustomerType, setSelectedCustomerType] = useState(() => searchParams.get('type') || '');
  const [selectedProduct, setSelectedProduct] = useState(() => searchParams.get('product') || '');
  // Affiliate filter: null = use default from currentAdmin, string = user has explicitly set a value
  const [affiliateFilterOverride, setAffiliateFilterOverride] = useState<string | null>(() => {
    const affiliateParam = searchParams.get('affiliate');
    // If there's a param in the URL, use it (even if empty string means "all")
    // If no param, return null to use the default behavior
    return affiliateParam !== null ? affiliateParam : null;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  }); // 1-based for display
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const toastShownRef = useRef(false);

  // Hide the page header
  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Show success toast after redirect from create/update
  useEffect(() => {
    const created = searchParams.get('created');
    const updated = searchParams.get('updated');

    // Only show toast if params exist and we haven't shown it yet
    if (created === 'true' && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.success('User created successfully!');
      // Clean up created/updated params but preserve filter params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('created');
      newParams.delete('updated');
      setSearchParams(newParams, { replace: true });
    } else if (updated === 'true' && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.success('User updated successfully!');
      // Clean up created/updated params but preserve filter params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('created');
      newParams.delete('updated');
      setSearchParams(newParams, { replace: true });
    } else if (!created && !updated) {
      // Reset ref when params are cleared
      toastShownRef.current = false;
    }
  }, [searchParams, setSearchParams]);

  // Sync filter state to URL params
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Only add non-empty filter values to URL
    if (searchQuery) newParams.set('q', searchQuery);
    if (organizationSearch) newParams.set('org', organizationSearch);
    if (selectedCustomerType) newParams.set('type', selectedCustomerType);
    if (selectedProduct) newParams.set('product', selectedProduct);
    // For affiliate filter, only add to URL if user has explicitly set it (override is not null)
    if (affiliateFilterOverride !== null && affiliateFilterOverride !== '') {
      newParams.set('affiliate', affiliateFilterOverride);
    }
    if (currentPage > 1) newParams.set('page', String(currentPage));

    // Use replace to avoid creating new history entries for every filter change
    setSearchParams(newParams, { replace: true });
  }, [searchQuery, organizationSearch, selectedCustomerType, selectedProduct, affiliateFilterOverride, currentPage, setSearchParams]);

  const pageSize = 20;

  // Determine user type and level
  const isLicenseAdmin = currentAdmin?.type === 'license_admin';
  const isProsciAdmin = currentAdmin?.type === 'prosci_admin';
  const isSuperAdmin = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'super';
  const isAffiliate = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'affiliate';
  const currentUserAffiliateId = currentAdmin?.affiliate_id;

  // Compute the default affiliate filter based on user type
  // - affiliate users: always their affiliate_id (locked)
  // - other users (including affiliate_admin): no default filter
  const defaultAffiliateFilter = useMemo(() => {
    if (isAffiliate && currentUserAffiliateId) {
      return String(currentUserAffiliateId);
    }
    return '';
  }, [isAffiliate, currentUserAffiliateId]);

  // The actual filter value:
  // - If user has explicitly set a value (override is not null), use that
  // - Otherwise use the default
  // - For affiliate users, always use their locked affiliate_id
  const effectiveAffiliateId = isAffiliate
    ? String(currentUserAffiliateId || '')
    : (affiliateFilterOverride !== null ? affiliateFilterOverride : defaultAffiliateFilter);

  // For UI display: what value should the dropdown show?
  const selectedAffiliate = isAffiliate
    ? String(currentUserAffiliateId || '')
    : (affiliateFilterOverride !== null ? affiliateFilterOverride : defaultAffiliateFilter);

  // Setter for the affiliate filter (used by dropdown and clearFilters)
  const setSelectedAffiliate = (value: string) => {
    setAffiliateFilterOverride(value);
  };

  // Query is ready when currentAdmin is loaded
  const affiliateFilterReady = !userLoading && !!currentAdmin;

  // Fetch filter options for Prosci admins and License admins
  // License admins get products filtered to only their organization's products via the API
  const { data: products } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: async () => {
      const result = await productService.listProducts({ active: true });
      return result as { data: Array<{ id: string; attributes: { name: string } }> };
    },
    enabled: isProsciAdmin || isLicenseAdmin,
    staleTime: 300000,
  });

  const { data: affiliates } = useQuery({
    queryKey: ['affiliates', 'active'],
    queryFn: async () => {
      const result = await affiliateService.listAffiliates();
      return result as { data: Array<{ id: string; attributes: { name: string } }> };
    },
    enabled: isProsciAdmin,
    staleTime: 300000,
  });

  // Fetch account data for license admins (for registration link)
  const { data: accountData } = useQuery({
    queryKey: ['account', currentAdmin?.organization_id],
    queryFn: () => accountService.getAccount(currentAdmin!.organization_id!),
    enabled: isLicenseAdmin && !!currentAdmin?.organization_id,
    staleTime: 300000,
  });

  // Fetch users
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-users', searchQuery?.trim() || '', organizationSearch?.trim() || '', selectedCustomerType,
               selectedProduct, effectiveAffiliateId, currentPage, isLicenseAdmin, currentAdmin?.organization_id],
    queryFn: () => {
      return userService.listUsers({
        q: searchQuery?.trim() || undefined,
        org_search: organizationSearch?.trim() || undefined,
        account_id: isLicenseAdmin ? currentAdmin?.organization_id : undefined,
        customer_type: selectedCustomerType ? parseInt(selectedCustomerType) : undefined,
        product_id: selectedProduct || undefined,
        affiliate_id: effectiveAffiliateId || undefined,
        limit: pageSize,
        skip: (currentPage - 1) * pageSize
      });
    },
    enabled: !userLoading && !!currentAdmin && affiliateFilterReady, // Only run query when user data is loaded and affiliate filter is ready
    staleTime: 0, // Always refetch when query key changes (filters change frequently)
  });

  // Fetch the count of Proxima projects the targeted user owns so the delete
  // confirmation modal can warn the admin about the reassignment impact.
  const { data: ownedProjectsData, isLoading: ownedProjectsLoading } = useQuery({
    queryKey: ['user-owned-proxima-projects-count', userToDelete?.id],
    queryFn: () => userService.getOwnedProximaProjectsCount(userToDelete!.id),
    enabled: !!userToDelete,
    staleTime: 0,
  });

  // Delete-user modal copy: hubAppCopy.portalCopy.userDeletion (hubAppCopy has
  // localized Object fields; same entry the portal uses for portalCopy).
  const { data: hubAppCopy } = useQuery({
    queryKey: ['contentful-hub-app-copy'],
    queryFn: () => contentfulService.getHubAppCopy(),
    staleTime: 1000 * 60 * 60,
  });

  const userDeletionCopy = hubAppCopy?.portalCopy?.userDeletion;
  const ownedProjectsCount = ownedProjectsData?.count ?? 0;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-users'] });
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      toast.error(
        error instanceof Error
          ? `Failed to delete user: ${error.message}`
          : 'Failed to delete user. Please try again.'
      );
    },
  });

  // Masquerade mutation (super admin only)
  const masqueradeMutation = useMutation({
    mutationFn: (userId: string) => userService.masqueradeAsUser(userId),
    onSuccess: (data) => {
      if (data.redirect_url) {
        toast.success(`Logging in as ${data.user.name}...`);
        // Small delay to show toast before redirect
        setTimeout(() => {
          window.location.href = data.redirect_url;
        }, 500);
      }
    },
    onError: (error) => {
      console.error('Masquerade failed:', error);
      toast.error(
        error instanceof Error
          ? `Failed to login as user: ${error.message}`
          : 'Failed to login as user. Please try again.'
      );
    },
  });

  // Extract users and pagination info
  const users = data?.data?.map(item => ({
    ...item.attributes,
    id: item.id
  })) || [];
  const totalCount = data?.meta?.total_count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserToDelete({
        id: userId,
        name: `${user.first_name} ${user.last_name}`
      });
    }
  };

  const handleMasqueradeAsUser = (userId: string) => {
    masqueradeMutation.mutate(userId);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setOrganizationSearch('');
    setSelectedCustomerType('');
    setSelectedProduct('');
    // Don't clear affiliate filter for affiliate users (it's locked)
    // For affiliate_admin, allow clearing the filter completely
    if (isAffiliate) {
      // Keep the locked filter
    } else {
      setSelectedAffiliate('');
    }
    setCurrentPage(1);
  };

  // hasFilters is true when any filter is actively limiting the results
  // For affiliate_admin: their default affiliate filter counts as a filter they can clear
  // For locked affiliate users: their affiliate filter is mandatory and doesn't count
  const hasFilters = Boolean(
    searchQuery ||
    organizationSearch ||
    selectedCustomerType ||
    selectedProduct ||
    (selectedAffiliate && !isAffiliate) // Any active affiliate filter for non-locked users
  );

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteMutation.mutateAsync(userToDelete.id);
      toast.success(`User ${userToDelete.name} deleted successfully`);
      setUserToDelete(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(
        error instanceof Error 
          ? `Failed to delete user: ${error.message}` 
          : 'Failed to delete user. Please try again.'
      );
    }
  };

  const getCustomerTypeLabel = (typeId: number): string => {
    switch (typeId) {
      case 1: return 'Direct';
      case 2: return 'License Admin';
      case 3: return 'License User';
      case 4: return 'Onsite';
      default: return 'Unknown';
    }
  };
  
  const handleCopyInviteLink = async () => {
    const account = accountData?.data?.attributes;
    const envPortalUrl = import.meta.env.VITE_PORTAL_URL;
    // Ensure base URL ends with a slash
    const portalBaseUrl = envPortalUrl 
      ? (envPortalUrl.endsWith('/') ? envPortalUrl : `${envPortalUrl}/`)
      : `${window.location.origin}/`;

    // Generate registration link using same logic as settings page
    let inviteLink: string = '';
    
    if (account?.registration_slug) {
      inviteLink = `${portalBaseUrl}register/${account.registration_slug}`;
    } else if (account?.name) {
      // Fallback to slugified name
      const slugifiedName = account.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      if (slugifiedName) {
        inviteLink = `${portalBaseUrl}register/${slugifiedName}`;
      }
    }
    
    if (!inviteLink) {
      toast.error('Unable to generate registration link. Account data not available.');
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success('Registration link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link. Please try again.');
    }
  };

  if (error) {
    return (
      <>
        <Header 
          title="Users" 
          size="small"
        />
        <ContentContainer>
          <Empty>
            <EmptyIcon variant="destructive">
              <AlertCircle />
            </EmptyIcon>
            <EmptyTitle>Error loading users</EmptyTitle>
            <EmptyDescription>
              {error instanceof Error ? error.message : 'Unknown error'}
            </EmptyDescription>
          </Empty>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      {/* Page Header */}
      <Header 
        title="Users" 
        size="small"
      >
        {/* Actions Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="lilac">
              Actions
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={() => navigate('/users/new')}
            >
              <UserPlus />
              Add User
            </DropdownMenuItem>
            {/* Show bulk upload for both Prosci admins and license admins */}
            <DropdownMenuItem
              onClick={() => navigate('/users/batch-upload')}
            >
              <Upload />
              Bulk Upload Users
            </DropdownMenuItem>
            {/* Bulk delete only for super admins */}
            {isSuperAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/users/bulk-delete')}
                  variant="destructive"
                >
                  <Trash2 />
                  Bulk Delete Users
                </DropdownMenuItem>
              </>
            )}
            {/* Copy invite link only for license admins */}
            {isLicenseAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleCopyInviteLink}
                >
                  <Copy  />
                  Copy Invite Link
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Header>
      
      {/* Filters */}
      <div className="bg-white border-b border-accent-sand">
        <div className="max-w-[1160px] px-6 py-4 mx-auto">
          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search Users */}
              <SearchInput
                id="search"
                label="Search Users"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />

              {/* User Type Filter */}
              <Field>
                <FieldLabel htmlFor="userType">User Type</FieldLabel>
                <Select
                  value={selectedCustomerType || ''}
                  onValueChange={(value) => {
                    setSelectedCustomerType(value || '');
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger id="userType" className="w-full">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLicenseAdmin ? (
                      // License admins can see license admins and license users
                      <>
                        <SelectItem value="2">License Admin</SelectItem>
                        <SelectItem value="3">License User</SelectItem>
                      </>
                    ) : (
                      // Prosci admins can see all types
                      <>
                        <SelectItem value="1">Direct</SelectItem>
                        <SelectItem value="2">License Admin</SelectItem>
                        <SelectItem value="3">License User</SelectItem>
                        <SelectItem value="4">Enterprise Onsite</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              
              {/* Product filter for both Prosci admins and License admins */}
              {/* License admins see only products from their organization */}
              {(isProsciAdmin || isLicenseAdmin) && products?.data && products.data.length > 0 && (
                <Field>
                  <FieldLabel htmlFor="product">Product</FieldLabel>
                  <Select
                    value={selectedProduct || ''}
                    onValueChange={(value) => {
                      setSelectedProduct(value || '');
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger id="product" className="w-full">
                      <SelectValue placeholder="All Products" />
                    </SelectTrigger>
                    <SelectContent>
                      {products?.data?.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.attributes?.name || 'Unknown Product'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {/* Additional filters for Prosci admins only */}
              {isProsciAdmin && (
                <>
                  <SearchInput
                    id="organization"
                    label="Organization"
                    iconSize="small"
                    value={organizationSearch}
                    onChange={(e) => {
                      setOrganizationSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search by organization..."
                  />

                  {/* Hide affiliate filter for affiliate users (their filter is locked) */}
                  {!isAffiliate && (
                    <Field>
                      <FieldLabel htmlFor="affiliate">Affiliate</FieldLabel>
                      <Select
                        value={effectiveAffiliateId || ''}
                        onValueChange={(value) => {
                          setSelectedAffiliate(value || '');
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger id="affiliate" className="w-full">
                          <SelectValue placeholder="All Affiliates" />
                        </SelectTrigger>
                        <SelectContent>
                          {affiliates?.data?.map((affiliate) => (
                            <SelectItem key={affiliate.id} value={affiliate.id}>
                              {affiliate.attributes?.name || 'Unknown Affiliate'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </>
              )}
            </div>
            
            {hasFilters && (
              <div className="flex items-center justify-end mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>

      <ContentContainer>
        {isLoading ? (
          <Empty>
            <EmptyIcon>
              <Spinner size="small" />
            </EmptyIcon>
            <EmptyTitle>Loading users</EmptyTitle>
            <EmptyDescription>
              Please wait while we fetch the user data...
            </EmptyDescription>
          </Empty>
        ) : users.length === 0 ? (
          <Empty>
            <EmptyIcon>
              <UserRoundX />
            </EmptyIcon>
            <EmptyTitle>No users found</EmptyTitle>
            <EmptyDescription>
              Try adjusting your search or filters to find users.
            </EmptyDescription>
            {hasFilters && (
              <Button
                type="button"
                variant="outline"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
          </Empty>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>User List</CardTitle>
              <CardDescription>
                Complete list of all portal users in your organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    {!isLicenseAdmin && (
                      <TableHead>Organization</TableHead>
                    )}
                    <TableHead>{isLicenseAdmin ? 'Type' : 'Customer Type'}</TableHead>
                    <TableHead className="text-center">Tool Count</TableHead>
                    <TableHead className="w-[80px]">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        {`${user.first_name} ${user.last_name}`}
                      </TableCell>
                      <TableCell>
                        {user.email}
                      </TableCell>
                      {!isLicenseAdmin && (
                        <TableCell>
                          {user.account_name || '—'}
                        </TableCell>
                      )}
                      <TableCell>
                        {getCustomerTypeLabel(user.customer_type_id)}
                      </TableCell>
                      <TableCell className="text-center">
                        {user.tool_count || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                            >
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                const returnParams = searchParams.toString();
                                navigate(`/users/${user.id}/edit${returnParams ? `?return=${encodeURIComponent(returnParams)}` : ''}`);
                              }}
                            >
                              <Edit />
                              Edit User
                            </DropdownMenuItem>
                            {isSuperAdmin && (
                              <DropdownMenuItem
                                onClick={() => handleMasqueradeAsUser(user.id)}
                                disabled={masqueradeMutation.isPending}
                              >
                                <UserCheck />
                                {masqueradeMutation.isPending ? 'Logging in...' : 'Login As'}
                              </DropdownMenuItem>
                            )}
                            {/* Delete is only available for super admins and license admins */}
                            {(isSuperAdmin || isLicenseAdmin) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDeleteUser(user.id)}
                                  variant="destructive"
                                >
                                  <Trash2 />
                                  Delete User
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 border-t pt-4 flex flex-col md:flex-row items-center justify-between gap-8">
                  <p className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
                  </p>
                  <Pagination className="w-auto mx-0">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(p => Math.max(1, p - 1));
                          }}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(pageNum);
                              }}
                              isActive={pageNum === currentPage}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      {totalPages > 5 && currentPage < totalPages - 2 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(p => Math.min(totalPages, p + 1));
                          }}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        <Dialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {userToDelete && (
              <div className="space-y-3 text-sm">
                {ownedProjectsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Spinner size="small" />
                    <span>Checking owned Proxima projects...</span>
                  </div>
                ) : ownedProjectsCount > 0 ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <p className="font-medium text-amber-900">
                      {(userDeletionCopy?.ownedProjectsLabel ||
                        'This user owns {count} Proxima project(s).'
                      ).replace('{count}', String(ownedProjectsCount))}
                    </p>
                    <p className="text-amber-900">
                      {userDeletionCopy?.reassignmentSummary ||
                        'These projects will be automatically reassigned to an eligible project admin or full collaborator with active Proxima access. If no eligible collaborator exists, the projects can be recovered by a Prosci support specialist.'}
                    </p>
                    {userDeletionCopy?.supportLinkUrl && (
                      <a
                        href={userDeletionCopy.supportLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-sm font-medium text-amber-900 underline hover:no-underline"
                      >
                        {userDeletionCopy.supportLinkText || 'Contact Prosci Support'}
                      </a>
                    )}
                  </div>
                ) : null}
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={() => setUserToDelete(null)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteUser}
                variant="destructive"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ContentContainer>
    </>
  );
}
